import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { DynamicForm } from '../dynamic-form/dynamic-form';
import { ItemDynamicForm } from '../item-dynamic-form/item-dynamic-form';
import { AssetService } from '../services/asset/asset.service';
import { MastersService } from '../services/masters/masters.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { GrnService } from '../services/vendor/grn.service';
import { PoService } from '../services/vendor/po.service';
import { ItemService } from '../services/item/item.service';
import { VendorService } from '../services/vendor/vendor.service';
import { S3FileUploadService, S3UploadResult } from '../services/s3/s3-file-upload.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom } from 'rxjs';
import { PayloadGeneratorService } from '../services/payload/payload-generator.service';
import { AssetCreatePayload } from './backend-payload.interface';
import { buildGrnProcurementFill, resolveGrnUnitRate, GrnRecord } from './grn-autofill.util';
import { ASSET_FIELD_TO_INTERFACE_MAP, ASSET_TABLE_TO_INTERFACE_MAP } from '../interfaces/asset-payload.interface';
import { QrPrintModalComponent } from './qr-print-modal.component';
import { AssetHistoryModalComponent } from './asset-history-modal.component';
import { PageActionService } from '../services/page-action/page-action.service';
import { STANDALONE_MODE } from '../utils/standalone.utils';

@Component({
  selector: 'lib-asset-create',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    ButtonModule, 
    TabsModule,
    DynamicForm,

    ItemDynamicForm,
    QrPrintModalComponent,
    AssetHistoryModalComponent
  ],
  templateUrl: './asset-create.html',
  styleUrl: './asset-create.css'
})
export class AssetCreate implements OnInit, OnDestroy {
  form: FormGroup;
  mode: 'create' | 'edit' | 'view' = 'create';
  private destroy$ = new Subject<void>();
  
  isLoading = false;
  isSubmitting = false;

  // Feature flag: standalone mode (asset module without accounting module)
  readonly STANDALONE_MODE = STANDALONE_MODE;
  // Fields to hide in standalone mode
  private readonly STANDALONE_HIDE_FIELDS = ['grn_id', 'quantity'];
  // Label->input in standalone: grn_no & purchase_order_no are autocomplete selectors instead; pi_no is an editable input a selection can fill.
  private readonly STANDALONE_LABEL_TO_INPUT = [
    'purchase_date', 'pi_no'
  ];
  // Label->autocomplete in standalone: grn_no = GRN selector (drives procurement fill); purchase_order_no = PO selector (fills vendor, narrows GRN list).
  private readonly STANDALONE_LABEL_TO_AUTOCOMPLETE = ['procurement_vendor_name', 'grn_no', 'purchase_order_no'];
  // Fields to convert from label to select in standalone mode
  private readonly STANDALONE_LABEL_TO_SELECT = ['subsidiary_id', 'location_id'];
  // Fields to make mandatory in standalone mode
  private readonly STANDALONE_MANDATORY_FIELDS = ['subsidiary_id', 'location_id'];
  // Fields to make visible in standalone mode (hidden by default in JSON)
  private readonly STANDALONE_SHOW_FIELDS = ['pi_no'];
  // Autocomplete fields to convert to input in standalone mode
  private readonly STANDALONE_CONVERT_TO_INPUT: string[] = [];
  // Select fields to convert to autocomplete in standalone mode (with search)
  private readonly STANDALONE_SELECT_TO_AUTOCOMPLETE = ['asset_name'];
  // In standalone mode, serial_number in item table should be editable input
  private readonly STANDALONE_TABLE_INPUT_FIELDS = ['serial_number'];

  allFields: any[] = [];
  primaryFields: any[] = [];
  primaryInfoExpanded = true;
  dynamicFormTabs: any[] = [];
  activeTabIndex: number = 0;
  
  templates: any[] = [];
  currentTemplateId: string | null = null;
  assetId: string | null = null;
  
  assetGroupOptions: any[] = [];
  parentAssetOptions: any[] = [];
  assetSubGroupMap: Map<string, any[]> = new Map();
  // Store actual IDs for label-display fields (standalone mode)
  private _assetGroupId: string | null = null;
  private _assetSubGroupId: string | null = null;
  // Item-master id of the Asset Name chosen in standalone mode; used to match a GRN line's rate.
  private _selectedItemMasterId: string | null = null;
  // PO number of the Purchase Order chosen in standalone mode; narrows the GRN search to that PO.
  private _selectedPoNumber: string | null = null;
  subsidiaryOptions: any[] = [];
  locationOptions: any[] = [];
  grnOptions: any[] = [];
  assetNameOptions: any[] = [];
  selectedGrnData: any = null;
  assetData: any = null;
  allGrnItems: any[] = [];
  subsidiaries: any[] = [];
  financialYearOptions: any[] = [];
  vendorOptions: any[] = [];
  maintenanceTypeOptions: any[] = [];

  // Table key constant
  readonly ASSET_ITEMS_TABLE_KEY = 'asset_items';

  // QR Print Modal
  qrPrintModalVisible = false;

  // Asset History Modal
  historyModalVisible = false;
  
  assetConditionOptions: any[] = [];
  depreciationTypeOptions: any[] = [];
  maintenancePeriodOptions: any[] = [];
  
  constructor(
    private fb: FormBuilder,
    private assetService: AssetService,
    private mastersService: MastersService,
    private subsidiaryService: SubsidiaryService,
    private router: Router,
    private route: ActivatedRoute,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef,
    private grnService: GrnService,
    private poService: PoService,
    private s3FileUploadService: S3FileUploadService,
    private payloadGenerator: PayloadGeneratorService,
    private itemService: ItemService,
    private vendorService: VendorService,
    private pageActionService: PageActionService
  ) {
    this.form = this.fb.group({});
  }

  async ngOnInit() {
    this.isLoading = true;
    this.generateFinancialYearOptions();
    
    await Promise.all([
      this.fetchMasters(),
      this.loadSubsidiaries()
    ]);
    
    try {
      const queryMode = this.route.snapshot.queryParamMap.get('mode') as 'create' | 'edit' | 'view' | null;
      this.mode = queryMode || this.route.snapshot.data['mode'] || 'create';
      const id = this.route.snapshot.paramMap.get('id');
      
      const templatesResp = await lastValueFrom(this.assetService.getFormTemplates());
      this.templates = templatesResp?.data || templatesResp || [];
      
      let templateIdToLoad: string | null = null;
      let assetData: any = null;
      
      if (id && this.mode !== 'create') {
        const response = await lastValueFrom(this.assetService.getAsset(id));
        if (response?.data) {
          assetData = response.data;
          this.assetData = assetData;
          if (assetData.form_template_id) {
            templateIdToLoad = String(assetData.form_template_id);
          }
        }
      }
      
      if (!templateIdToLoad) {
        const defaultTemplate = this.templates.find((t: any) => t.is_default === true) || this.templates[0];
        if (defaultTemplate) {
          templateIdToLoad = String(defaultTemplate.id);
        }
      }
      
      if (templateIdToLoad) {
        this.currentTemplateId = templateIdToLoad;
        const templateResp = await lastValueFrom(
          this.assetService.getFormTemplateById(this.currentTemplateId)
        );
        
        if (templateResp?.data?.fields) {
          this.allFields = templateResp.data.fields;
          const tabs = templateResp.data.tabs || [];
          this.processFields(this.allFields, tabs);
          this.initializeForm();
          this.setupCustomFormField();
        }
      }
      
      if (assetData) {
        this.patchFormWithAsset(assetData);
      }
      
    } catch (err) {
      console.error('Error loading asset data', err);
      this.toasterService.error('Failed to load form template');
    } finally {
      this.isLoading = false;
      this.setupPageActions();
      this.cdr.detectChanges();
    }
    
    this.pageActionService.handleActionsUntil(this.destroy$, {
      recover: () => this.navigateToRecover(),
      assign: () => this.navigateToAssign(),
      edit: () => this.onEdit(),
      print: () => this.openQRPrintModal()
    });
  }

  ngOnDestroy() {
    this.pageActionService.setActions([]);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupPageActions() {
    if (this.mode === 'view' || this.mode === 'edit') {
      const actions: any[] = [];

      const assetStatus = this.assetData?.status;

      // Show Recover for assigned assets, Assign for active assets, neither for disposed/other
      if (assetStatus === 'assigned' && this.assetData?.active_assignment_id && (this.assetData?.assigned_to_user_id || this.assetData?.assigned_to_department_id)) {
        actions.push({ id: 'recover', label: 'Recover', icon: 'pi pi-refresh', severity: 'danger', showInToolbar: false });
      } else if (assetStatus === 'active') {
        actions.push({ id: 'assign', label: 'Assign', icon: 'pi pi-user-plus', showInToolbar: false });
      }

      actions.push(
        { id: 'print', label: 'Print QR', icon: 'pi pi-print', showInToolbar: false },
        { id: 'edit', label: 'Edit', icon: 'pi pi-pencil', showInToolbar: false }
      );
      this.pageActionService.setActions(actions);
    } else {
      this.pageActionService.setActions([]);
    }
  }

  onEdit() {
    this.mode = 'edit';
    this.form.enable();
    this.setupPageActions();
    this.cdr.markForCheck();
  }

  async fetchMasters() {
    try {
      const res = await lastValueFrom(this.mastersService.getMasters([
        'asset-groups', 
        'asset-sub-groups', 
        'maintenance-types', 
        'asset-condition-options', 
        'depreciation-type-options', 
        'maintenance-period-options'
      ]));
      if (res?.data) {
        if (res.data['asset-groups']) {
          this.assetGroupOptions = (res.data['asset-groups'] || []).map((ag: any) => ({
            label: ag.name, value: ag.id, ...ag
          }));
        }
        if (res.data['asset-sub-groups']) {
          const subGroups = res.data['asset-sub-groups'] || [];
          this.assetSubGroupMap.clear();
          subGroups.forEach((sg: any) => {
            const key = String(sg.asset_group_id);
            if (!this.assetSubGroupMap.has(key)) this.assetSubGroupMap.set(key, []);
            this.assetSubGroupMap.get(key)!.push({ label: sg.name, value: sg.id, ...sg });
          });
        }
        if (res.data['maintenance-types']) {
          this.maintenanceTypeOptions = (res.data['maintenance-types'] || []).map((mt: any) => ({
             label: mt.name, value: mt.id, ...mt
          }));
        }
        if (res.data['asset-condition-options']) {
          this.assetConditionOptions = res.data['asset-condition-options'];
        }
        if (res.data['depreciation-type-options']) {
          this.depreciationTypeOptions = res.data['depreciation-type-options'];
        }
        if (res.data['maintenance-period-options']) {
          this.maintenancePeriodOptions = res.data['maintenance-period-options'];
        }
        if (this.allFields.length > 0) {
          this.assignOptionsToFields(this.allFields);
          this.cdr.markForCheck();
        }
      }
    } catch (error) {
      console.error('Error fetching masters:', error);
    }
  }

  async loadSubsidiaries() {
    try {
      const res = await lastValueFrom(this.subsidiaryService.getSubsidiaries());
      const data = res?.data?.subsidiaries || res?.data || res || [];
      this.subsidiaries = Array.isArray(data) ? data : [];
      this.subsidiaryOptions = this.subsidiaries.map((s: any) => ({ label: s.name, value: s.id, ...s }));
      if (this.allFields.length > 0) {
        this.assignOptionsToFields(this.allFields);
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Error loading subsidiaries:', error);
    }
  }

  assignOptionsToFields(fields: any[]) {
    fields.forEach(f => {
      if ((f.field_name === 'asset_group_id') && this.assetGroupOptions.length > 0) {
        f.options = this.assetGroupOptions;
      }
      if ((f.field_name === 'subsidiary_id') && this.subsidiaryOptions.length > 0) {
        f.options = this.subsidiaryOptions;
      }
      if (f.field_name === 'location_id' && this.locationOptions.length > 0) {
        f.options = this.locationOptions;
      }
      if (f.field_name === 'asset_condition') {
        f.options = this.assetConditionOptions;
      }
      if (f.field_name === 'depreciation_type') {
        f.options = this.depreciationTypeOptions;
      }
      if (f.field_name === 'maintenance_period') {
        f.options = this.maintenancePeriodOptions;
      }
      if (f.field_name === 'financial_year') {
        f.options = this.financialYearOptions;
      }
      if (f.field_name === 'maintenance_type_id') {
         f.options = this.maintenanceTypeOptions;
      }
      if (f.options && Array.isArray(f.options) && f.options.length > 0) {
        f.options = this.normalizeOptions(f.options);
      }
    });
  }

  generateFinancialYearOptions() {
    const startYear = 2015;
    const currentYear = new Date().getFullYear();
    const options = [];
    
    for (let year = currentYear; year >= startYear; year--) {
      const yy = year.toString().slice(-2);
      const nextYy = (year + 1).toString().slice(-2);
      const label = `FY ${yy}-${nextYy}`;
      options.push({ label: label, value: label });
    }
    
    this.financialYearOptions = options;
  }

  setOptionsForField(fieldName: string, options: any[]) {
    const normalize = this.normalizeOptions(options);
    const field = this.allFields.find(f => f.field_name === fieldName);
    if (field) {
      field.options = normalize;
    }
    this.cdr.markForCheck();
  }

  normalizeOptions(options: any[]): any[] {
    if (!Array.isArray(options)) return [];
    return options.map(opt => {
      if (typeof opt === 'string') return { label: opt, value: opt };
      return {
        label: opt.label || opt.name || opt.display_name || String(opt.value || opt.id),
        value: opt.value !== undefined ? opt.value : opt.id,
        ...opt
      };
    });
  }

  processFields(fields: any[], tabs: any[] = []) {
    this.assignOptionsToFields(fields);

    // Standalone mode: adjust fields for standalone asset module
    if (this.STANDALONE_MODE) {
      fields.forEach(f => {
        // Hide fields not needed in standalone mode
        if (this.STANDALONE_HIDE_FIELDS.includes(f.field_name)) {
          f.is_visible = false;
        }
        // Show fields that are hidden by default but needed in standalone
        if (this.STANDALONE_SHOW_FIELDS.includes(f.field_name)) {
          f.is_visible = true;
        }
        // Convert label fields to input for manual entry (not in view mode)
        if (this.mode !== 'view' && this.STANDALONE_LABEL_TO_INPUT.includes(f.field_name)) {
          if (f.field_type === 'label') {
            f.field_type = f.data_type === 'date' ? 'date' : 'input';
          }
        }
        // Convert label fields to autocomplete with vendor search (not in view mode)
        if (this.mode !== 'view' && this.STANDALONE_LABEL_TO_AUTOCOMPLETE.includes(f.field_name)) {
          if (f.field_type === 'label') {
            f.field_type = 'autocomplete';
            f.options = [];
          }
        }
        // Convert label fields to select dropdowns (not in view mode)
        if (this.mode !== 'view' && this.STANDALONE_LABEL_TO_SELECT.includes(f.field_name)) {
          if (f.field_type === 'label') {
            f.field_type = 'select';
          }
        }
        // Make fields mandatory in standalone mode
        if (this.STANDALONE_MANDATORY_FIELDS.includes(f.field_name)) {
          f.is_mandatory = true;
        }
        // Convert autocomplete to input (not in view mode)
        if (this.mode !== 'view' && this.STANDALONE_CONVERT_TO_INPUT.includes(f.field_name) && f.field_type === 'autocomplete') {
          f.field_type = 'input';
        }
        // Convert select to autocomplete for item master search (not in view mode)
        if (this.mode !== 'view' && this.STANDALONE_SELECT_TO_AUTOCOMPLETE.includes(f.field_name) && f.field_type === 'select') {
          f.field_type = 'autocomplete';
        }
        // Make table fields editable in standalone mode (not in view mode)
        if (this.mode !== 'view' && this.STANDALONE_TABLE_INPUT_FIELDS.includes(f.field_name)) {
          if (f.field_type === 'label') {
            f.field_type = 'input';
          }
        }
      });
    }

    if (this.mode === 'create') {
      fields.forEach(f => {
        if (f.field_name === 'asset_code') f.is_visible = false;
        if (f.field_name === 'asset_date') f.default_value = new Date();
        if (f.field_name === 'current_asset_value') f.is_visible = false;
      });
    } else {
      // Logic for Update/View mode

      if (this.STANDALONE_MODE) {
        // In standalone mode, add serial_number and model_no after asset_name in primary tab
        const assetNameField = fields.find(f => f.field_name === 'asset_name');
        if (assetNameField) {
          fields.push(
            {
              field_name: 'serial_number',
              display_name: 'Serial Number',
              field_type: 'label',
              data_type: 'string',
              tab_id: assetNameField.tab_id,
              sub_tab_id: assetNameField.sub_tab_id,
              display_order: assetNameField.display_order + 0.1,
              is_active: true,
              is_visible: true,
              width: '1'
            },
            {
              field_name: 'model_no',
              display_name: 'Model No.',
              field_type: 'label',
              data_type: 'string',
              tab_id: assetNameField.tab_id,
              sub_tab_id: assetNameField.sub_tab_id,
              display_order: assetNameField.display_order + 0.2,
              is_active: true,
              is_visible: true,
              width: '1'
            }
          );
        }
      } else {
        // Move grn_no to grn_id position (only when accounting module is available)
        const grnIdField = fields.find(f => f.field_name === 'grn_id');
        const grnNoField = fields.find(f => f.field_name === 'grn_no');

        if (grnIdField && grnNoField) {
          grnNoField.tab_id = grnIdField.tab_id;
          grnNoField.sub_tab_id = grnIdField.sub_tab_id;
          grnNoField.display_order = grnIdField.display_order;
        }

        // Add serial_number and model_no label fields after grn_no
        if (grnNoField) {
          fields.push(
            {
              field_name: 'serial_number',
              display_name: 'Serial Number',
              field_type: 'label',
              data_type: 'string',
              tab_id: grnNoField.tab_id,
              sub_tab_id: grnNoField.sub_tab_id,
              display_order: grnNoField.display_order + 0.1,
              is_active: true,
              is_visible: true,
              width: '1'
            },
            {
              field_name: 'model_no',
              display_name: 'Model No.',
              field_type: 'label',
              data_type: 'string',
              tab_id: grnNoField.tab_id,
              sub_tab_id: grnNoField.sub_tab_id,
              display_order: grnNoField.display_order + 0.2,
              is_active: true,
              is_visible: true,
              width: '1'
            }
          );
        }
      }

      // Filter out grn_id and quantity fields
      fields = fields.filter(f => f.field_name !== 'grn_id' && f.field_name !== 'quantity');

      // Remove tab with system_name 'asset_details' and its fields
      const assetTabs = tabs.filter(t => t.system_name === 'asset_details');
      if (assetTabs.length > 0) {
        const assetTabIds = assetTabs.map((t: any) => t.tab_id);
        // Remove tabs
        tabs = tabs.filter(t => t.system_name !== 'asset_details');
        // Remove fields belonging to those tabs
        fields = fields.filter(f => !assetTabIds.includes(f.tab_id));
      }
      // Change field type to 'label' for asset_name and asset_date
      const fieldsToLabel = ['asset_name', 'asset_date'];
      fields.forEach(f => {
        if (fieldsToLabel.includes(f.field_name)) {
          f.field_type = 'label';
        }
      });
    }

    this.primaryFields = fields.filter(f => f.tab_id === 1).sort((a, b) => a.display_order - b.display_order);
    
    // Filter active tabs - exclude Tagging tab in create mode (it's only for view/edit)
    let activeTabs = tabs.filter(tab => tab.is_active !== false && tab.tab_id > 1).sort((a, b) => a.display_order - b.display_order);
    
    if (this.mode === 'create') {
      // Remove Tagging tab in create mode
      activeTabs = activeTabs.filter(tab => tab.system_name !== 'tagging' && tab.tab_id !== 7);
      // Also filter out tagging fields
      fields = fields.filter(f => f.tab_id !== 7);
    }
    
    this.dynamicFormTabs = activeTabs.map((tab, index) => {
      const tabFields = fields.filter(f => f.tab_id === tab.tab_id && f.is_active !== false).sort((a, b) => a.display_order - b.display_order);
      
      const activeSubTabs = (tab.sub_tabs || []).filter((st: any) => st.is_active !== false).map((subTab: any) => {
        const subTabFields = tabFields.filter(f => f.sub_tab_id === subTab.sub_tab_id || f.sub_tab_id === undefined);
        return {
          name: subTab.display_name || subTab.system_name,
          key: subTab.key,
          sub_type: subTab.type,
          content: subTabFields,
          show_heading: subTab.show_heading ?? false
        };
      });
      
      return { title: tab.display_name || tab.system_name, value: index, sub_tabs: activeSubTabs };
    }).filter(tab => tab.sub_tabs.length > 0);
  }

  private initializeForm(preserveValues: any = null) {
    const currentValues = preserveValues || this.form.value;
    this.form = this.fb.group({});
    
    const addControl = (field: any) => {
      const key = this.getFieldKey(field.field_name);
      if (!this.form.contains(key)) {
        const defaultValue = currentValues[key] ?? field.default_value ?? '';
        const control = this.fb.control(defaultValue);
        
        // Disable non-editable fields in edit/view mode
        const nonEditableFields = ['asset_date', 'form_template_id'];
        if (this.mode !== 'create' && nonEditableFields.includes(field.field_name)) {
          control.disable();
        }
        
        this.form.addControl(key, control);
      }
    };

    this.primaryFields.forEach(field => addControl(field));
    
    if (this.dynamicFormTabs.length > 0) {
      this.dynamicFormTabs.forEach(tab => {
        tab.sub_tabs.forEach((subTab: any) => {
          if (subTab.sub_type === 'TABLE' || subTab.sub_type === 'table') {
            const tableKey = subTab.key || 'asset_items';
            const itemsArray = this.fb.array([]);
            this.form.addControl(tableKey, itemsArray);
            
            const itemsData = currentValues[tableKey];
            if (itemsData && Array.isArray(itemsData)) {
              itemsData.forEach((row: any) => {
                const rowGroup = this.fb.group({});
                Object.keys(row).forEach(k => rowGroup.addControl(k, this.fb.control(row[k])));
                (itemsArray as any).push(rowGroup);
              });
            }

            // In standalone create mode, add one empty row to asset_items table
            if (this.STANDALONE_MODE && this.mode === 'create' && tableKey === this.ASSET_ITEMS_TABLE_KEY && itemsArray.length === 0) {
              const emptyRow = this.fb.group({});
              (subTab.content || []).forEach((field: any) => {
                emptyRow.addControl(this.getFieldKey(field.field_name), this.fb.control(''));
              });
              // itemsArray.push(emptyRow);
            }
          } else {
            (subTab.content || []).forEach((field: any) => addControl(field));
          }
        });
      });
    }

    // Ensure procurement_vendor_id hidden control exists
    const vendorIdKey = this.getFieldKey('procurement_vendor_id');
    if (!this.form.contains(vendorIdKey)) {
      this.form.addControl(vendorIdKey, this.fb.control(currentValues[vendorIdKey] || ''));
    }
  }

  private resetFormExcept(preservedKeys: string[]) {
    if (!this.form || !this.form.controls) return;
    
    Object.keys(this.form.controls).forEach(key => {
      if (!preservedKeys.includes(key)) {
        const control = this.form.get(key);
        if (control instanceof FormArray) {
          control.clear();
        } else if (control) {
          control.patchValue(null, { emitEvent: false });
        }
      }
    });
  }

  getFieldKey(fieldName: string): string { return fieldName; }

  private setupCustomFormField() {
    const customFormField = this.primaryFields.find(f => f.field_name === 'form_template_id');
    if (!customFormField) return;
    customFormField.options = this.normalizeOptions(this.templates);
    if (this.currentTemplateId) {
      customFormField.default_value = this.currentTemplateId;
      const ctrl = this.form?.get(this.getFieldKey('form_template_id'));
      if (ctrl && !ctrl.value) ctrl.patchValue(this.currentTemplateId);
    }
  }

  private patchFormWithAsset(asset: any) {
    this.assetId = asset.id ? String(asset.id) : null;
    
    const getFieldValue = (fieldName: string) => {
      if (asset[fieldName] !== undefined) return asset[fieldName];
      if (asset.detail && asset.detail[fieldName] !== undefined) return asset.detail[fieldName];
      if (asset.custom_fields?.[fieldName] !== undefined) return asset.custom_fields[fieldName];
      return null;
    };

    const patchField = (field: any) => {
      const key = this.getFieldKey(field.field_name);
      let value = getFieldValue(field.field_name);
      if (field.data_type === 'date' && value) value = new Date(value);
      if (value !== undefined && value !== null) this.form.get(key)?.patchValue(value);
    };

    this.primaryFields.forEach(field => patchField(field));

    // parent asset display in edit/view mode
    if (asset.parent_asset_id && asset.parent_asset) {
      const option = {
        label: asset.parent_asset,
        value: asset.parent_asset_id
      };

      this.parentAssetOptions = [option];
      this.setOptionsForField('parent_asset', this.parentAssetOptions);

      this.form.get(this.getFieldKey('parent_asset'))
          ?.patchValue(asset.parent_asset, { emitEvent: false });

      this.form.get(this.getFieldKey('parent_asset_id'))
          ?.patchValue(asset.parent_asset_id, { emitEvent: false });
    }

    // Procurement vendor display in edit/view mode
    const procVendorName = getFieldValue('procurement_vendor_name');
    const procVendorId = getFieldValue('procurement_vendor_id');
    if (procVendorName) {
      const vendorOption = {
        label: procVendorName,
        value: procVendorId || procVendorName
      };
      this.setOptionsForField('procurement_vendor_name', [vendorOption]);
      this.form.get(this.getFieldKey('procurement_vendor_name'))
          ?.patchValue(procVendorName, { emitEvent: false });
      if (procVendorId) {
        this.form.get(this.getFieldKey('procurement_vendor_id'))
            ?.patchValue(String(procVendorId), { emitEvent: false });
      }
    }

    this.dynamicFormTabs.forEach(tab => {
      tab.sub_tabs.forEach((subTab: any) => {
        if (subTab.sub_type !== 'TABLE' && subTab.sub_type !== 'table') {
          (subTab.content || []).forEach((field: any) => patchField(field));
        }
      });
    });

    // Populate maintenance_schedule table with maintenance_items from API response
    if (asset.maintenance_items && Array.isArray(asset.maintenance_items) && asset.maintenance_items.length > 0) {
      this.populateMaintenanceScheduleTable(asset.maintenance_items);
    }

    // Populate QR Code tab fields
    const tagPrefix = getFieldValue('tag_prefix') || '';
    
    // Set tag_prefix_display field value
    const tagPrefixControl = this.form.get(this.getFieldKey('tag_prefix_display'));
    if (tagPrefixControl) {
      tagPrefixControl.patchValue(tagPrefix || '-', { emitEvent: false });
    }
    
    // In standalone mode, resolve asset_group_id and asset_sub_group_id IDs to display names (labels, non-editable)
    const agId = getFieldValue('asset_group_id');
    const asgId = getFieldValue('asset_sub_group_id');
    if (this.STANDALONE_MODE) {
      if (agId) {
        this._assetGroupId = String(agId);
        const group = this.assetGroupOptions.find((o: any) => String(o.value) === String(agId));
        if (group) {
          this.form.get(this.getFieldKey('asset_group_id'))?.patchValue(group.label, { emitEvent: false });
        }
      }
      if (asgId && agId) {
        this._assetSubGroupId = String(asgId);
        const subGroups = this.assetSubGroupMap.get(String(agId)) || [];
        const subGroup = subGroups.find((o: any) => String(o.value) === String(asgId));
        if (subGroup) {
          this.form.get(this.getFieldKey('asset_sub_group_id'))?.patchValue(subGroup.label, { emitEvent: false });
        }
      }
    }

    // Resolve subsidiary_id and location_id to display names in view mode (in edit mode they are selects)
    if (this.mode === 'view') {
      const subId = getFieldValue('subsidiary_id');
      if (subId) {
        const sub = this.subsidiaryOptions.find((o: any) => String(o.value) === String(subId));
        if (sub) {
          this.form.get(this.getFieldKey('subsidiary_id'))?.patchValue(sub.label, { emitEvent: false });
        }
      }
      const locId = getFieldValue('location_id');
      if (locId && subId) {
        const subsidiary = this.subsidiaries.find((s: any) => String(s.id) === String(subId));
        const taxInfo = subsidiary?.tax_information || [];
        const loc = taxInfo.find((t: any) => String(t.id) === String(locId));
        if (loc) {
          this.form.get(this.getFieldKey('location_id'))?.patchValue(loc.address, { emitEvent: false });
        }
      }
    }

    // Trigger dependent field updates
    const assetGroupId = getFieldValue('asset_group_id');
    if (assetGroupId) {
       this.onFormFieldChanged({ key: this.getFieldKey('asset_group_id'), value: assetGroupId });
    }

    const subsidiaryId = getFieldValue('subsidiary_id');
    if (subsidiaryId) {
      this.handleSubsidiaryChange(subsidiaryId);
    }
    if (this.mode === 'view') this.form.disable();
  }

  onFormFieldChanged(evt: { key: string; value: any; [k: string]: any }) {
    if (!evt) return;
    
    if (evt.key === this.getFieldKey('form_template_id')) {
      const templateId = typeof evt.value === 'object' ? evt.value.value : evt.value;
      this.handleFormTemplateChange(templateId);
      return;
    }
    
    if (evt.key === this.getFieldKey('subsidiary_id')) {
      const subsidiaryId = typeof evt.value === 'object' ? evt.value.value : evt.value;
      this.handleSubsidiaryChange(subsidiaryId);
      return;
    }

    if (evt.key === this.getFieldKey('asset_group_id')) {
      const assetGroupId = typeof evt.value === 'object' ? String(evt.value.value || evt.value.id) : String(evt.value);
      if (assetGroupId) {
        const subGroups = this.assetSubGroupMap.get(assetGroupId) || [];
        this.setOptionsForField('asset_sub_group_id', subGroups);
      } else {
        this.setOptionsForField('asset_sub_group_id', []);
      }
      return;
    }

    if (evt.key === this.getFieldKey('grn_id')) {
      const grnId = typeof evt.value === 'object' ? (evt.value.value || evt.value.id) : evt.value;
      if (grnId) {
        this.handleGRNSelect(grnId);
      } else {
        this.resetFormExcept(['asset_date', 'form_template_id', 'grn_id']);
        this.selectedGrnData = null;
        this.allGrnItems = [];
        this.assetNameOptions = [];
        this.setOptionsForField('asset_name', []);
      }
      return;
    }

    if (evt.key === this.getFieldKey('asset_name')) {
      if (this.STANDALONE_MODE) {
        // In standalone mode, auto-fill fields from selected item master data
        const selected = evt.value;
        if (selected && typeof selected === 'object') {
          const itemName = selected.item_master_name || selected.name || selected.label;
          // Capture the chosen Asset Name's item-master id; resolveGrnUnitRate matches it to a GRN line's item_id (else falls through to single-line/blank — never a wrong rate).
          const selectedItemId = selected.item_id ?? selected.id ?? selected.value;
          this._selectedItemMasterId = selectedItemId != null ? String(selectedItemId) : null;
          // Set value as the name string (not ID)
          this.form.get(this.getFieldKey('asset_name'))?.patchValue(itemName, { emitEvent: false });

          // Auto-fill related fields from item master
          const updates: Record<string, any> = {};
          if (selected.asset_group_id) {
            this._assetGroupId = String(selected.asset_group_id);
            const group = this.assetGroupOptions.find((o: any) => String(o.value) === String(selected.asset_group_id));
            updates['asset_group_id'] = group ? group.label : selected.asset_group_id;
            const subGroups = this.assetSubGroupMap.get(String(selected.asset_group_id)) || [];
            this.setOptionsForField('asset_sub_group_id', subGroups);
          }
          if (selected.asset_sub_group_id) {
            this._assetSubGroupId = String(selected.asset_sub_group_id);
            const subGroups = this.assetSubGroupMap.get(String(selected.asset_group_id)) || [];
            const subGroup = subGroups.find((o: any) => String(o.value) === String(selected.asset_sub_group_id));
            updates['asset_sub_group_id'] = subGroup ? subGroup.label : selected.asset_sub_group_id;
          }
          if (selected.asset_type) {
            updates['asset_type'] = selected.asset_type;
          }
          if (selected.description) {
            updates['description'] = selected.description;
          } else if (itemName) {
            updates['description'] = itemName;
          }
          if (selected.model_no) {
            updates['model_no'] = selected.model_no;
          }

          Object.keys(updates).forEach(key => {
            const control = this.form.get(this.getFieldKey(key));
            if (control) {
              control.patchValue(updates[key], { emitEvent: false });
            }
          });
          this.cdr.markForCheck();
        } else {
          // Asset Name cleared/retyped to a non-selection — drop the stale item id so it can't leak into a later GRN rate match.
          this._selectedItemMasterId = null;
        }
        return;
      }

      const itemId = typeof evt.value === 'object' ? (evt.value.value || evt.value.id) : evt.value;
      if (itemId) {
        this.handleAssetNameSelect(itemId);
      }
      return;
    }

    if (evt.key === this.getFieldKey('parent_asset')) {
      const selected = evt.value;

      if (selected && typeof selected === 'object') {
        const id = selected.value || selected.id;
        const name = selected.name || selected.label;

        // Set hidden ID field
        this.form.get(this.getFieldKey('parent_asset_id'))
            ?.patchValue(id, { emitEvent: false });

        // Set visible name field properly
        this.form.get(this.getFieldKey('parent_asset'))
            ?.patchValue(name, { emitEvent: false });
      } else {
        this.form.get(this.getFieldKey('parent_asset_id'))
            ?.patchValue(null, { emitEvent: false });
      }
      return;
    }

    if (evt.key === this.getFieldKey('procurement_vendor_name')) {
      const selected = evt.value;
      if (selected && typeof selected === 'object') {
        const vendorId = selected.value || selected.id;
        const vendorDisplay = selected.label || `${selected.vendor_code} - ${selected.company_name}`;

        // Store vendor_code - company_name as the display string
        this.form.get(this.getFieldKey('procurement_vendor_name'))
            ?.patchValue(vendorDisplay, { emitEvent: false });

        // Store vendor ID in procurement_vendor_id
        if (!this.form.contains(this.getFieldKey('procurement_vendor_id'))) {
          this.form.addControl(this.getFieldKey('procurement_vendor_id'), this.fb.control(''));
        }
        this.form.get(this.getFieldKey('procurement_vendor_id'))
            ?.patchValue(String(vendorId), { emitEvent: false });
      }
      return;
    }

    if (evt.key === this.getFieldKey('grn_no')) {
      const selected = evt.value;
      // Only a dropdown object-selection drives the fill; typed/cleared string values are ignored.
      if (selected && typeof selected === 'object') {
        this.handleGRNNoSelect(selected);
      }
      return;
    }

    if (evt.key === this.getFieldKey('purchase_order_no')) {
      const selected = evt.value;
      if (selected && typeof selected === 'object') {
        this.handlePONoSelect(selected);
      } else {
        // PO cleared/retyped to a non-selection — drop the stored PO so it stops narrowing GRNs.
        this._selectedPoNumber = null;
      }
      return;
    }
  }

  onSearchQuery(evt: { fieldName: string; query: string; field?: any }) {
    if (!evt?.fieldName || !evt?.query) return;

    if (evt.fieldName === 'grn_id') {
      this.handleGRNSearch(evt.query);
      return;
    }

    if (evt.fieldName === 'grn_no') {
      this.handleGRNSearch(evt.query, 'grn_no');
      return;
    }

    if (evt.fieldName === 'purchase_order_no') {
      this.handlePOSearch(evt.query);
      return;
    }

    if (evt.fieldName === 'parent_asset') {
      this.handleParentAssetSearch(evt.query);
      return;
    }

    if (evt.fieldName === 'procurement_vendor_name') {
      this.handleProcurementVendorSearch(evt.query);
      return;
    }

    if (evt.fieldName === 'asset_name' && this.STANDALONE_MODE) {
      this.handleItemMasterSearch(evt.query);
      return;
    }
  }

  async handleItemMasterSearch(query: string) {
    try {
      const res = await lastValueFrom(this.itemService.searchItems({ name: query, limit: 20, is_deleted: false, category_id: 1, item_type: 2 }));
      const items = res?.data?.items || [];
      const options = items.map((item: any) => ({
        label: item.item_master_name || item.name || item.item_name,
        value: item.item_master_name || item.name || item.item_name,
        id: item.id,
        ...item
      }));
      this.setOptionsForField('asset_name', options);
    } catch (err) {
      console.error('Item master search failed', err);
    }
  }

  async handleVendorSearch(query: string, fieldName: string, rowGroup?: FormGroup) {
    try {
      const res = await lastValueFrom(this.vendorService.getAllVendors({ query, limit: 20 }));
      let options: any[] = [];
      if (res?.data?.vendors) {
        options = res.data.vendors.map((v: any) => ({
          label: `${v.vendor_code} - ${v.company_name}`,
          value: v.id,
          ...v
        }));
      }

      if (rowGroup) {
        const subTab = this.dynamicFormTabs.find(t => t.sub_tabs.some((st: any) => st.key === 'maintenance_schedule'))
                        ?.sub_tabs.find((st: any) => st.key === 'maintenance_schedule');
        
        if (subTab && subTab.content) {
             const vendorField = subTab.content.find((f: any) => f.field_name === 'vendor_name');
             if(vendorField) {
                 vendorField.options = options;
                 this.cdr.markForCheck();
             }
        }

      } else {
        this.vendorOptions = options;
        this.setOptionsForField(fieldName, options);
      }
      
    } catch (err) {
      console.error('Error searching vendors', err);
    }
  }

  async handleProcurementVendorSearch(query: string) {
    try {
      const res = await lastValueFrom(this.vendorService.getAllVendors({ query, limit: 20 }));
      let options: any[] = [];
      if (res?.data?.vendors) {
        options = res.data.vendors.map((v: any) => ({
          label: `${v.vendor_code} - ${v.company_name}`,
          value: v.id,
          ...v
        }));
      }
      this.setOptionsForField('procurement_vendor_name', options);
    } catch (err) {
      console.error('Procurement vendor search failed', err);
    }
  }

  private async handleFormTemplateChange(templateId: any) {
    if (!templateId || templateId === this.currentTemplateId) return;
    
    try {
      this.isLoading = true;
      const currentValues = this.form.value;
      const templateResp = await lastValueFrom(this.assetService.getFormTemplateById(String(templateId)));
      
      if (templateResp?.data?.fields) {
        this.currentTemplateId = String(templateId);
        this.allFields = templateResp.data.fields;
        const tabs = templateResp.data.tabs || [];
        this.processFields(this.allFields, tabs);
        this.initializeForm(currentValues);
        this.setupCustomFormField();
        this.form?.get(this.getFieldKey('form_template_id'))?.patchValue(templateId, { emitEvent: false });
      }
    } catch (e) {
      console.error('Failed to change form template', e);
      this.toasterService.error('Failed to load template');
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  handleSubsidiaryChange(subsidiaryId: any) {
    if (!subsidiaryId) {
      this.locationOptions = [];
      this.setOptionsForField('location_id', []);
      return;
    }
    
    const subsidiary = this.subsidiaries.find((s: any) => 
      String(s.id) === String(subsidiaryId) || String(s.value) === String(subsidiaryId)
    );
    
    if (!subsidiary) {
      this.locationOptions = [];
      this.setOptionsForField('location_id', []);
      return;
    }
    
    const taxInfo = subsidiary.tax_information || [];
    this.locationOptions = taxInfo.map((t: any) => ({
      label: t.address,
      value: t.id
    }));
    this.setOptionsForField('location_id', this.locationOptions);
  }

  async handleGRNSearch(query: string, targetField: string = 'grn_id') {
    try {
      // Integrated mode: vendor from vendor_id (grn_id path); standalone: vendor from procurement_vendor_id (grn_no path).
      const vendorIdKey = this.getFieldKey(
        targetField === 'grn_no' ? 'procurement_vendor_id' : 'vendor_id'
      );
      const vendorId = this.form.get(vendorIdKey)?.value;
      const vId = (vendorId && typeof vendorId === 'object') ? (vendorId.value || vendorId.id) : vendorId;

      const params: any = {
        limit: 20,
        grn_number: query,
        for_asset: true
      };

      if (vId) {
        params.vendor_id = vId;
      }

      // If a PO was selected on the form, narrow the GRN list to that PO (filter at every step).
      if (targetField === 'grn_no' && this._selectedPoNumber) {
        params.po_number = this._selectedPoNumber;
      }

      const res = await lastValueFrom(this.grnService.getGrns(params, false));

      if (res?.data) {
        const items = res.data.grns || res.data.items || res.data || [];
        this.grnOptions = items.map((g: any) => ({
          label: g.receipt_no || g.grn_no || g.reference_no || g.id,
          value: g.id,
          ...g,
          name: g.receipt_no || g.grn_no
        }));
        this.setOptionsForField(targetField, this.grnOptions);
      }
    } catch (err) {
      console.error('Error searching GRNs', err);
    }
  }

  async handleGRNSelect(grnId: string | number) {
    try {
      this.resetFormExcept(['asset_date', 'form_template_id', 'grn_id']);

      this.selectedGrnData = null;
      this.allGrnItems = [];
      this.assetNameOptions = [];
      this.setOptionsForField('asset_name', []);

      const res = await lastValueFrom(this.grnService.getGrnById(grnId, { for_asset: true }));
      if (res?.data) {
        this.selectedGrnData = res.data;
        const items = (res.data.grn_items || []).filter((item: any) => item.item_type === 2);
        
        // Store all GRN items (not grouped) for table population
        this.allGrnItems = items;
        
        // Group items by item_id and create options for asset_name
        const itemMap = new Map<string, any>();
        items.forEach((item: any) => {
          const itemId = String(item.item_id);
          if (!itemMap.has(itemId)) {
            itemMap.set(itemId, {
              label: item.item_name || `Item ${itemId}`,
              value: itemId,
              item_id: itemId,
              item_name: item.item_name,
              quantity: item.quantity,
              ...item
            });
          } else {
            // Aggregate quantity for grouped items with same item_id
            const existing = itemMap.get(itemId);
            existing.quantity = (existing.quantity) + (item.quantity);
          }
        });
        
        this.assetNameOptions = Array.from(itemMap.values());
        this.setOptionsForField('asset_name', this.assetNameOptions);
         // Populate Procurement fields from GRN Data
         const procurementFieldsToPopulate: Record<string, any> = {
          'procurement_vendor_name': res.data?.vendor,
          'grn_no': res.data.receipt_no || res.data.grn_no, 
          'purchase_order_no': res.data.po_number || res.data.purchase_order_no,
          'purchase_date': res.data?.created_date,
         };

         Object.entries(procurementFieldsToPopulate).forEach(([fieldName, value]) => {
            const fieldKey = this.getFieldKey(fieldName);
            const control = this.form.get(fieldKey);
            if (control && value !== undefined && value !== null && value !== '') {
                control.patchValue(value, { emitEvent: false });
            }
         });

        // Set subsidiary_id (center) from GRN data
        const subsidiaryId = res.data.subsidiary_id;
        if (subsidiaryId) {
          const subsidiaryKey = this.getFieldKey('subsidiary_id');
          const subsidiaryControl = this.form.get(subsidiaryKey);
          if (subsidiaryControl) {
            subsidiaryControl.patchValue(subsidiaryId, { emitEvent: false });
          }
          // Trigger subsidiary change to load location options
          this.handleSubsidiaryChange(subsidiaryId);
        }

        // Set location_id from GRN data (after locations are loaded)
        const locationId = res.data.location;
        if (locationId) {
          const locationKey = this.getFieldKey('location_id');
          const locationControl = this.form.get(locationKey);
          if (locationControl) {
            locationControl.patchValue(locationId, { emitEvent: false });
          }
          this.cdr.markForCheck();
        }
      }
    } catch (err) {
      console.error('Error fetching GRN details', err);
      this.toasterService.error('Failed to fetch GRN details');
    }
  }

  async handleGRNNoSelect(grn: any) {
    try {
      // Fetch full GRN detail for line items (per-unit rate); fall back to the option object if no id or no detail.
      let detail: GrnRecord = grn;
      const grnId = grn?.value ?? grn?.id;
      if (grnId) {
        const res = await lastValueFrom(this.grnService.getGrnById(grnId, { for_asset: true }));
        if (res?.data) {
          detail = res.data;
        }
      }

      // Show the GRN number as the field's display value (autocomplete may hold the object).
      const grnNo = detail.receipt_no || detail.grn_no || grn?.label;
      if (grnNo) {
        this.form.get(this.getFieldKey('grn_no'))?.patchValue(grnNo, { emitEvent: false });
      }

      // Procurement display fields (vendor, PO no, PI no, purchase date).
      const fill = buildGrnProcurementFill(detail);
      Object.entries(fill).forEach(([fieldName, value]) => {
        const control = this.form.get(this.getFieldKey(fieldName));
        if (control && value !== undefined && value !== null && value !== '') {
          control.patchValue(value, { emitEvent: false });
        }
      });

      // Vendor id (hidden control) so a later GRN search stays vendor-scoped.
      const vendorId = detail.vendor_id;
      if (vendorId !== undefined && vendorId !== null) {
        const vendorIdKey = this.getFieldKey('procurement_vendor_id');
        if (!this.form.contains(vendorIdKey)) {
          this.form.addControl(vendorIdKey, this.fb.control(''));
        }
        this.form.get(vendorIdKey)?.patchValue(String(vendorId), { emitEvent: false });
      }

      // Subsidiary + Center. Setting subsidiary must re-run its cascade to load Center options.
      const subsidiaryId = detail.subsidiary_id;
      if (subsidiaryId) {
        this.form.get(this.getFieldKey('subsidiary_id'))?.patchValue(subsidiaryId, { emitEvent: false });
        this.handleSubsidiaryChange(subsidiaryId);
      }
      const locationId = detail.location;
      if (locationId) {
        this.form.get(this.getFieldKey('location_id'))?.patchValue(locationId, { emitEvent: false });
      }

      // Best-effort per-unit value (spec §4). Leaves the field blank when it cannot be resolved.
      const rate = resolveGrnUnitRate(detail.grn_items, this._selectedItemMasterId);
      if (rate !== null) {
        this.form.get(this.getFieldKey('purchase_bill_value'))?.patchValue(rate, { emitEvent: false });
      }

      this.cdr.markForCheck();
    } catch (err) {
      console.error('Error fetching GRN details', err);
      this.toasterService.error('Failed to fetch GRN details');
    }
  }

  // Search purchase orders for the Purchase Order No. dropdown, scoped to the selected vendor.
  async handlePOSearch(query: string) {
    try {
      const vendorId = this.form.get(this.getFieldKey('procurement_vendor_id'))?.value;
      const vId = (vendorId && typeof vendorId === 'object') ? (vendorId.value || vendorId.id) : vendorId;

      const params: any = {
        limit: 20,
        get_references: 'true',
        po_no: query
      };
      if (vId) {
        params.vendor_id = String(vId);
      }

      const res = await lastValueFrom(this.poService.getAllPurchaseOrders(params));
      const items = res?.data?.vendor_pos || res?.data?.purchase_orders || res?.data?.items || res?.data || [];
      const options = (Array.isArray(items) ? items : []).map((p: any) => ({
        label: p.po_number || p.po_no || p.reference_no || p.id,
        value: p.id,
        ...p,
        name: p.po_number || p.po_no
      }));
      this.setOptionsForField('purchase_order_no', options);
    } catch (err) {
      console.error('Error searching purchase orders', err);
    }
  }

  // On selecting a PO: show its number, fill vendor, narrow the GRN dropdown to this PO; no Asset Name change / form reset, fields stay editable.
  async handlePONoSelect(po: any) {
    try {
      const poNo = po.po_number || po.po_no || po.label;
      if (poNo) {
        this.form.get(this.getFieldKey('purchase_order_no'))?.patchValue(poNo, { emitEvent: false });
      }
      // Remember the PO number so a later GRN search is pre-filtered to this PO.
      this._selectedPoNumber = poNo ? String(poNo) : null;

      // Fill vendor from the PO when present.
      const vendorName = po.Vendor?.company_name || po.vendor || po.vendor_name;
      const vendorId = po.vendor_id ?? po.Vendor?.id;
      if (vendorName) {
        this.form.get(this.getFieldKey('procurement_vendor_name'))?.patchValue(vendorName, { emitEvent: false });
      }
      if (vendorId !== undefined && vendorId !== null) {
        const vendorIdKey = this.getFieldKey('procurement_vendor_id');
        if (!this.form.contains(vendorIdKey)) {
          this.form.addControl(vendorIdKey, this.fb.control(''));
        }
        this.form.get(vendorIdKey)?.patchValue(String(vendorId), { emitEvent: false });
      }

      // Clear stale GRN options so the next GRN search re-fetches, now narrowed to this PO.
      this.setOptionsForField('grn_no', []);

      this.cdr.markForCheck();
    } catch (err) {
      console.error('Error selecting purchase order', err);
    }
  }

  async handleParentAssetSearch(query: string) {
    try {
      const subsidiaryId = this.form.get(this.getFieldKey('subsidiary_id'))?.value;
      const locationId = this.form.get(this.getFieldKey('location_id'))?.value;
      const grnId = this.form.get(this.getFieldKey('grn_id'))?.value;
      const assetName = this.form.get(this.getFieldKey('asset_name'))?.value;

      // Basic guard for search 
      if (!subsidiaryId || !locationId) {
        this.toasterService.warn('Please select GRN first');
        return; 
      }

      // In CREATE mode we need GRN+Asset name selected for parent search
      if (this.mode === 'create') {
        if (!grnId || !assetName) {
          this.toasterService.warn('Please select Asset Name first');
          return;
        }
      }

      const res = await lastValueFrom(
        this.assetService.getAssets({
          search: query,
          limit: 10,
          subsidiary_id: subsidiaryId,
          location_id: locationId
        })
      );

      if (res?.data?.assets) {
        this.parentAssetOptions = res.data.assets
        .filter((a: any) => this.mode !== 'edit' || String(a.id) !== String(this.assetId))
        .map((a: any) => ({
          label: `${a.asset_code} — ${a.asset_name}`,
          value: a.id,
          name: a.asset_name
        }));

        this.setOptionsForField('parent_asset', this.parentAssetOptions);
      }
    } catch (err) {
      console.error('Error searching parent assets', err);
      this.toasterService.error('Failed to search parent assets');
    }
  }

  async handleAssetNameSelect(itemId: string | number) {
    const selectedItem = this.assetNameOptions.find(
      (item: any) => String(item.value) === String(itemId) || String(item.item_id) === String(itemId)
    );
    
    if (selectedItem) {
      // Populate form fields with selected item data
      const fieldsToPopulate: Record<string, any> = {
        'quantity': selectedItem.quantity,
      };

      // Patch form values for pre-populating fields
      Object.entries(fieldsToPopulate).forEach(([fieldName, value]) => {
        const fieldKey = this.getFieldKey(fieldName);
        const control = this.form.get(fieldKey);
        if (control && value !== undefined && value !== null) {
          control.patchValue(value, { emitEvent: false });
        }
      });

      // Populate asset_items table with rows from GRN items matching this item_id
      this.populateAssetItemsTable(String(itemId));
    }

    // Fetch item details from Item Master to populate other fields
    try {
      this.isLoading = true;
      const itemResp = await lastValueFrom(this.itemService.getItemMasterWithDetails(itemId));
      if (itemResp?.data) {
        const item = itemResp.data;
        
        const updates: any = {};
        
        if (item.asset_group_id) {
          this._assetGroupId = String(item.asset_group_id);
          if (this.STANDALONE_MODE) {
            const group = this.assetGroupOptions.find((o: any) => String(o.value) === String(item.asset_group_id));
            updates.asset_group_id = group ? group.label : item.asset_group_id;
          } else {
            updates.asset_group_id = item.asset_group_id;
          }

          const subGroups = this.assetSubGroupMap.get(String(item.asset_group_id)) || [];
          this.setOptionsForField('asset_sub_group_id', subGroups);

          if (item.asset_sub_group_id) {
            this._assetSubGroupId = String(item.asset_sub_group_id);
            if (this.STANDALONE_MODE) {
              const subGroup = subGroups.find((o: any) => String(o.value) === String(item.asset_sub_group_id));
              updates.asset_sub_group_id = subGroup ? subGroup.label : item.asset_sub_group_id;
            } else {
              updates.asset_sub_group_id = item.asset_sub_group_id;
            }
          }
        }

        if (item.description) {
          updates.description = item.description;
        } else if (item.name) {
          updates.description = item.name;
        }

        if (item.asset_type) {
          updates.asset_type = item.asset_type;
        }
        
        if (item.model_no) {
          updates.model_no = item.model_no;
        }

        Object.keys(updates).forEach(key => {
          const fieldKey = this.getFieldKey(key);
          const control = this.form.get(fieldKey);
          if (control) {
            control.patchValue(updates[key], { emitEvent: false });
          }
        });
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private populateAssetItemsTable(itemId: string) {
    // Filter GRN items matching the selected item_id
    const matchingItems = this.allGrnItems.filter(
      (item: any) => String(item.item_id) === itemId
    );

    if (matchingItems.length === 0) return;

    // Get or create the FormArray for asset_items table
    let tableFormArray = this.form.get(this.ASSET_ITEMS_TABLE_KEY) as FormArray;
    
    if (!tableFormArray) {
      tableFormArray = this.fb.array([]);
      this.form.addControl(this.ASSET_ITEMS_TABLE_KEY, tableFormArray);
    }

    // Clear existing rows
    tableFormArray.clear();

    // Create a row for each GRN item (based on quantity)
    matchingItems.forEach((grnItem: any) => {
      const quantity = grnItem.quantity;
      
      // Create rows equal to the quantity of this GRN item line
      for (let i = 0; i < quantity; i++) {
        const rowGroup = this.fb.group({
          [this.getFieldKey('serial_number')]: [grnItem.serial_no || ''],
          [this.getFieldKey('model_no')]: [''],
          [this.getFieldKey('tag_prefix')]: [''],
          [this.getFieldKey('qr_code')]: [''],
          // Store GRN item reference
          grn_item_id: [grnItem.id],
          inventory_id: [grnItem.inventory_id]
        });
        tableFormArray.push(rowGroup);
      }
    });

    this.cdr.markForCheck();
  }

  /**
   * Populate maintenance_schedule table with maintenance_items from API response during edit/view mode
   */
  private populateMaintenanceScheduleTable(maintenanceItems: any[]) {
    const tableKey = 'maintenance_schedule';
    
    // Get or create the FormArray for maintenance_schedule table
    let tableFormArray = this.form.get(tableKey) as FormArray;
    
    if (!tableFormArray) {
      tableFormArray = this.fb.array([]);
      this.form.addControl(tableKey, tableFormArray);
    }

    // Clear existing rows
    tableFormArray.clear();

    // Create a row for each maintenance item
    maintenanceItems.forEach((item: any) => {
      const rowGroup = this.fb.group({
        id: [item.id || ''],
        vendor_id: [item.vendor_id || ''],
        vendor_name: [item.vendor_name || ''],
        scheduled_date: [item.scheduled_date ? new Date(item.scheduled_date) : null],
        maintenance_remarks: [item.maintenance_remarks || '']
      });
      tableFormArray.push(rowGroup);
    });

    this.cdr.markForCheck();
  }

  getTableFormArray(key: string): FormArray {
    return this.form.get(key) as FormArray || this.fb.array([]);
  }
  
  onItemFieldChanged(event: any) {
    if (event.fieldName === 'vendor_name') {
       const selectedValue = event.fieldValue?.value;
       let vendorId: any;
       let vendorName: string = '';

       if (selectedValue && typeof selectedValue === 'object') {
          vendorId = selectedValue.value || selectedValue.id;
          vendorName = selectedValue.label || `${selectedValue.vendor_code} - ${selectedValue.company_name}`;
       }

       if (event.key && typeof event.rowIndex === 'number') {
           const formArray = this.form.get(event.key) as FormArray;
           const rowGroup = formArray ? formArray.at(event.rowIndex) as FormGroup : null;

           if (rowGroup) {
               // Update vendor_id (hidden field)
               const vendorIdControl = rowGroup.get('vendor_id');
               if (vendorIdControl && vendorId) {
                   vendorIdControl.patchValue(vendorId, { emitEvent: false });
               }
               
               const vendorNameControl = rowGroup.get('vendor_name');
               if (vendorNameControl) {
                   if (vendorNameControl.value !== vendorName) {
                       vendorNameControl.setValue(vendorName, { emitEvent: false });
                   }
               }
           }
       }
    }
  }

  onItemSearchQuery(event: any, subTab: any) {
    if (subTab.key === 'maintenance_schedule' && event?.fieldName === 'vendor_name') {
       this.handleVendorSearch(event.query, 'vendor_name', event.rowGroup);
    }
  }

  onRowDuplicated(event: any, subTab: any) {
  }

  async onSubmit() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.isLoading = true;
    
    try {
      const formValue = this.form.getRawValue();
      
      if (this.mode === 'create') {
        const payload = this.buildCreatePayload(formValue);
        const response = await lastValueFrom(this.assetService.createAssets(payload));
        
        if (response?.data || response?.success) {
          this.toasterService.success('Assets created successfully');
          this.router.navigate(['/assets']);
        } else {
          throw new Error(response?.message || 'Failed to create assets');
        }
      } else if (this.mode === 'edit' && this.assetId) {
        const payload = this.buildUpdatePayload(formValue);
        const response = await lastValueFrom(this.assetService.updateAsset(this.assetId, payload));
        
        if (response?.data || response?.status == "success") {
          this.toasterService.success('Asset updated successfully');
          this.router.navigate(['/assets']);
        } else {
          throw new Error(response?.message || 'Failed to update asset');
        }
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      this.toasterService.error(error.message || 'Failed to save asset');
    } finally {
      this.isSubmitting = false;
      this.isLoading = false;
    }
  }

  private buildCreatePayload(formValue: any): AssetCreatePayload {
    
    // helper to extract table FormArrays
    const tableFormArrays = new Map<string, FormArray>();
    
    // Explicitly add known tables
    const itemsArray = this.form.get(this.ASSET_ITEMS_TABLE_KEY) as FormArray;
    if (itemsArray) {
      tableFormArrays.set(this.ASSET_ITEMS_TABLE_KEY, itemsArray);
    }

    // Attempt to find other tables from dynamic tabs
    this.dynamicFormTabs.forEach(tab => {
        tab.sub_tabs?.forEach((subTab: any) => {
            if (subTab.sub_type === 'TABLE' || subTab.sub_type === 'table') {
                const key = subTab.key || subTab.name;
                if (key && !tableFormArrays.has(key)) {
                    const control = this.form.get(key);
                    if (control instanceof FormArray) {
                        tableFormArrays.set(key, control);
                    }
                }
            }
        });
    });

    return this.payloadGenerator.generatePayload<AssetCreatePayload>({
      formGroup: this.form,
      fields: this.allFields,
      dynamicFormTabs: this.dynamicFormTabs,
      fieldToInterfaceMap: ASSET_FIELD_TO_INTERFACE_MAP,
      tableToInterfaceMap: ASSET_TABLE_TO_INTERFACE_MAP,
      tableFormArrays: tableFormArrays,
      arrayKeys: ['items', 'maintenance_schedule'],
      extractObjectId: (value: any, fieldName: string) => {
        // Handle object values (autocomplete/select)
        if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
           return String(value.id ?? value.value ?? value.item_id ?? value);
        }
        
        // Ensure ID fields are strings
        const idFields = [
          'grn_id', 'asset_group_id', 'asset_sub_group_id',
          'subsidiary_id', 'location_id', 'parent_asset_id',
          'form_template_id', 'item_id', 'vendor_id',
          'procurement_vendor_id', 'financial_year_id', 'maintenance_type_id',
          'inventory_id', 'grn_item_id'
        ];
        
        if (idFields.includes(fieldName) && value !== null && value !== undefined && value !== '') {
            return String(value);
        }
        
        return value;
      },
      transformPayload: (payload) => {
        return this.transformCreatePayload(payload);
      }
    });
  }

  private transformCreatePayload(payload: any): any {
    // Flatten primary if exists (from tab 1)
    if (payload.primary) {
      Object.assign(payload, payload.primary);
      delete payload.primary;
    }
    
    if (this.STANDALONE_MODE) {
      // In standalone mode, send asset_name as-is (string) and don't send grn_id/item_id
      delete payload.grn_id;
      delete payload.item_id;
    } else if (payload.asset_name) {
      payload.item_id = String(payload.asset_name);
      delete payload.asset_name;
    }
    
    // Extract procurement_vendor_name string from autocomplete object
    if (payload.procurement?.procurement_vendor_name && typeof payload.procurement.procurement_vendor_name === 'object') {
      payload.procurement.procurement_vendor_name = payload.procurement.procurement_vendor_name.label || payload.procurement.procurement_vendor_name.company_name || '';
    }

    // In standalone mode, replace label strings with actual IDs for asset_group_id / asset_sub_group_id
    if (this.STANDALONE_MODE) {
      if (this._assetGroupId) {
        payload.asset_group_id = this._assetGroupId;
      }
      if (this._assetSubGroupId) {
        payload.asset_sub_group_id = this._assetSubGroupId;
      }
    }

    // Transform Financial
    if (payload.financial) {
      const f = payload.financial;
      if (f.depreciation_rate) f.depreciation_rate = Number(f.depreciation_rate);
      if (f.asset_life_years) f.asset_life_years = Number(f.asset_life_years);
    }

    // Transform Maintenance
    if (payload.maintenance) {
      const m = payload.maintenance;

      // Handle nested items if they exist at root
      const items = payload.maintenance_items || payload.maintenance_schedule;
      
      if (items) {
        m.items = items;
        if (payload.maintenance_items) delete payload.maintenance_items;
        if (payload.maintenance_schedule) delete payload.maintenance_schedule;
      }
    } else if (payload.maintenance_schedule) {
        // If maintenance object was missing but schedule exists (shouldn't happen if structure is correct, but safe fallback)
        payload.maintenance = {
            items: payload.maintenance_schedule
        };
        delete payload.maintenance_schedule;
    }
    return payload;
  }

  private buildUpdatePayload(formValue: any): any {
    // Initialize payload with nested objects matching backend AssetUpdate struct
    const payload: any = {
      procurement: {},
      financial: {},
      warranty_amc: {},
      maintenance: {},
      custom_fields: {}
    };

    // Fields that expect boolean values in backend
    const booleanFields = ['is_movable', 'is_periodic_maintenance', 'maint_from_purchase_date', 'maint_continue_after_amc'];
    
    // Fields that are numeric
    const numericFields = ['purchase_bill_value', 'depreciation_rate', 'asset_life_years'];

    // Helper to format date values using local date (not UTC) to avoid timezone issues
    const formatDate = (value: any): string => {
      if (!value) return '';
      if (value instanceof Date) {
        // Use local date methods to avoid timezone conversion issues
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      if (typeof value === 'string' && value.includes('T')) {
        return value.split('T')[0];
      }
      return value;
    };

    // Helper to extract ID from object or return value
    const extractValue = (value: any): any => {
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        return value.value ?? value.id ?? value;
      }
      return value;
    };

    // Helper to process field value based on type
    const processValue = (fieldName: string, value: any, dataType: string): any => {
      // Skip empty strings for optional fields
      if (value === '' || value === null || value === undefined) {
        return undefined;
      }

      // Handle date fields
      if (dataType === 'date') {
        return formatDate(value);
      }

      // Handle boolean fields
      if (booleanFields.includes(fieldName)) {
        if (typeof value === 'boolean') return value;
        if (value === 'true' || value === '1') return true;
        if (value === 'false' || value === '0') return false;
        return undefined;
      }

      // Handle numeric fields
      if (numericFields.includes(fieldName)) {
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      }

      // Extract value from objects (autocomplete/select)
      return extractValue(value);
    };

    // Process all fields and route them to correct location
    const processField = (field: any) => {
      const key = this.getFieldKey(field.field_name);
      const rawValue = formValue[key];
      const processedValue = processValue(field.field_name, rawValue, field.data_type);

      if (processedValue === undefined) return;

      if (field.is_custom_field) {
        payload.custom_fields[field.field_name] = processedValue;
        return;
      }

      // Get field mapping to determine destination
      const mapping = ASSET_FIELD_TO_INTERFACE_MAP[field.field_name];

      if (mapping === undefined || mapping === '') {
        // Root level field
        payload[field.field_name] = processedValue;
      } else if (mapping === 'procurement') {
        payload.procurement[field.field_name] = processedValue;
      } else if (mapping === 'financial') {
        payload.financial[field.field_name] = processedValue;
      } else if (mapping === 'warranty_amc') {
        payload.warranty_amc[field.field_name] = processedValue;
      } else if (mapping === 'maintenance') {
        payload.maintenance[field.field_name] = processedValue;
      }
    };

    // Process primary fields
    this.primaryFields.forEach(field => processField(field));
    
    // Process dynamic tab fields
    this.dynamicFormTabs.forEach(tab => {
      tab.sub_tabs.forEach((subTab: any) => {
        if (subTab.sub_type !== 'TABLE' && subTab.sub_type !== 'table') {
          (subTab.content || []).forEach((field: any) => processField(field));
        } else if (subTab.key === 'maintenance_schedule') {
          // Handle maintenance_schedule table
          const maintenanceScheduleArray = this.form.get('maintenance_schedule') as FormArray;
          if (maintenanceScheduleArray && maintenanceScheduleArray.length > 0) {
            const maintenanceItems = maintenanceScheduleArray.controls.map((row: any) => {
              const rowValue = row.value;
              return {
                id: rowValue.id || undefined,
                vendor_id: rowValue.vendor_id ? String(rowValue.vendor_id) : undefined,
                vendor_name: rowValue.vendor_name || '',
                scheduled_date: formatDate(rowValue.scheduled_date),
                maintenance_remarks: rowValue.maintenance_remarks || ''
              };
            });
            
            payload.maintenance.items = maintenanceItems;
          }
        }
      });
    });
    
    if (Object.keys(payload.custom_fields).length === 0) delete payload.custom_fields;

    // Extract procurement_vendor_name string from autocomplete object
    if (payload.procurement?.procurement_vendor_name && typeof payload.procurement.procurement_vendor_name === 'object') {
      payload.procurement.procurement_vendor_name = payload.procurement.procurement_vendor_name.label || payload.procurement.procurement_vendor_name.company_name || '';
    }

    // In standalone mode, replace label strings with actual IDs for asset_group_id / asset_sub_group_id
    if (this.STANDALONE_MODE) {
      if (this._assetGroupId) {
        payload.asset_group_id = this._assetGroupId;
      }
      if (this._assetSubGroupId) {
        payload.asset_sub_group_id = this._assetSubGroupId;
      }
    }

    return payload;
  }

  // =============================================================================
  // File Upload Handling
  // =============================================================================

  async onFileUploadRequested(event: any, uploadMode: 'form' | 'table' = 'form', tableKey?: string) {
    event.mode = uploadMode;
    if (tableKey) event.tableKey = tableKey;
    await this.handleFileUpload(event);
  }

  async onFileDownloadRequested(event: any) {
    const { s3Key } = event;
    if (!s3Key) return;
    try {
      // Use assetId if available, otherwise 0.
      const entityId = this.assetId ? Number(this.assetId) : 0;
      await this.s3FileUploadService.openPreviewUrl(entityId, s3Key, 'asset');
    } catch (err: any) {
      this.toasterService.error(err.message || 'Failed to open file');
    }
  }

  private async handleFileUpload(event: any): Promise<void> {
    const { file, field } = event;
    if (!file) return;

    this.isLoading = true;
    try {
      const assetIdForUpload = this.assetId || '0';
      const result = await this.s3FileUploadService.uploadFileToS3(
        file,
        assetIdForUpload,
        'asset'
      );

      // Apply the upload result based on mode
      if (event.mode === 'form') {
        this.applyFormFieldUpload(result, field);
      }

      this.toasterService.success('File uploaded successfully');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to upload file';
      this.toasterService.error(errorMessage);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private applyFormFieldUpload(result: S3UploadResult, field: any): void {
    const fileFieldKey = this.getFieldKey(field.field_name);
    const control = this.form.get(fileFieldKey);
    if (control) {
        control.patchValue(result.s3Key);
        control.markAsDirty();
        control.markAsTouched();
    }
  }

  goBack() {
    this.router.navigate(['/assets']);
  }

  // =============================================================================
  // QR Print Modal
  // =============================================================================

  openQRPrintModal(): void {
    if (this.assetId) {
      this.qrPrintModalVisible = true;
    }
  }

  onQRPrinted(newCount: number): void {
    this.toasterService.success(`QR code printed successfully (Total prints: ${newCount})`);
  }

  // =============================================================================
  // Asset History Modal
  // =============================================================================

  openHistoryModal(): void {
    if (this.assetId) {
      this.historyModalVisible = true;
    }
  }

  // =============================================================================
  // Button Click Handler (from dynamic-form)
  // =============================================================================

  onButtonClicked(event: { fieldName: string; actionName: string; field: any }): void {
    switch (event.actionName) {
      case 'print_qr_code':
        this.openQRPrintModal();
        break;
      default:
    }
  }

  navigateToRecover() {
    if (this.assetId && this.assetData?.active_assignment_id && (this.assetData?.assigned_to_user_id || this.assetData?.assigned_to_department_id)) {
      this.router.navigate(['/assets', this.assetId, 'recover'], {
        queryParams: {
          asset_assignment_id: this.assetData.active_assignment_id
        }
      });
    }
  }

  navigateToAssign() {
    if (this.assetId) {
      this.router.navigate(['/assets', this.assetId, 'assign']);
    }
  }
}

